import type { Condition } from 'types/condition';
import { useEffect, useRef } from 'react';
import { Paragraph } from '@leafwell/components';
import LinkArrow from './link-arrow';
import LinkBadge from './link-badge';
import HeadingBody from './heading-body';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDraggableScroll } from 'hooks/useDraggableScroll';
import { useMatchMedia } from 'hooks/useMatchMedia';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';

type ConditionListVariant = 'default' | 'minimal' | 'boxed';

type ItemProps = Condition & {
  isActive?: boolean;
  isLast?: boolean;
  isMinimal?: boolean;
  updateScroll?: (scrollValue: number) => void;
};

const ConditionItem = ({
  id,
  link,
  title,
  isActive,
  isLast,
  isMinimal,
  updateScroll,
}: ItemProps) => {
  const lisItemRef = useRef<HTMLLIElement>();
  const lastItemRef = useRef<HTMLAnchorElement>();

  useEffect(() => {
    if (isActive && lisItemRef?.current) {
      const scrollDistance =
        lisItemRef.current.offsetLeft -
        window.innerWidth / 2 +
        lisItemRef.current.offsetWidth / 2;

      updateScroll(scrollDistance);
    }
  }, [isActive]);

  useIntersectionObserver(
    (entry, observer) => {
      if (entry?.isIntersecting) {
        observer?.disconnect();
      }
    },
    isLast && lastItemRef,
    {
      threshold: 0.5,
    },
  );
  const { trackEvent } = useEventsTracker();

  return (
    <li
      key={id}
      className="animate-fade-in flex-shrink-0 mr-3"
      {...(isMinimal && isActive ? { ref: lisItemRef } : {})}
      onClick={() =>
        trackEvent({
          event: 'select_content',
          content_type: title,
          item_id: 'medical_condition',
          origin: isMinimal ? 'condition_single_page' : 'conditions_list_page',
        })
      }
    >
      {isMinimal ? (
        <LinkBadge
          href={link}
          active={isActive}
          className="no-drag"
          {...(isLast ? { ref: lastItemRef } : {})}
        >
          {title}
        </LinkBadge>
      ) : (
        <LinkArrow
          className="!grid-cols-conditions-grid"
          title={title}
          uri={link}
        />
      )}
    </li>
  );
};

const ConditionsList = <T extends Partial<ItemProps>>({
  activeCondition,
  className = '',
  conditions,
  loading,
  renderItem,
  title,
  variant = 'default',
}: {
  activeCondition?: string;
  className?: string;
  conditions?: T[];
  loading?: boolean;
  renderItem?: (item: T) => JSX.Element;
  resetScroll?: boolean;
  title?: string;
  variant?: ConditionListVariant;
}): JSX.Element => {
  const isMinimal = variant === 'minimal';
  const transitionClassNames = [
    'transition-opacity',
    loading ? 'opacity-0' : '',
    className,
  ].join(' ');
  const dragEl = useRef(null);
  const { onMouseDown, updateScroll } = useDraggableScroll({
    ref: dragEl,
    direction: 'horizontal',
  });
  const { translations } = useWordPressContext();
  const [isMediumScreen] = useMatchMedia(['(min-width: 768px)'], [true]);
  const listInlineStyle =
    isMinimal || !isMediumScreen
      ? {}
      : {
          gridTemplateRows: `repeat(${Math.ceil(
            conditions?.length / 2,
          )}, 0.5fr)`,
        };
  const listClassNames: string[] = ['transition-opacity'];

  if (variant === 'minimal') {
    listClassNames.push(
      'relative cursor-grab flex overflow-x-auto leafwell-purple-scrollbar pt-1 pb-2',
    );
  } else {
    listClassNames.push(
      'grid md:grid-cols-2 md:grid-flow-col gap-y-2 gap-x-10 content-start items-start',
    );
  }

  return (
    <>
      {title && (
        <HeadingBody className={transitionClassNames} level="4" title={title} />
      )}
      {conditions?.length > 0 ? (
        <ul
          className={[
            listClassNames.join(' '),
            loading ? 'pointer-events-none' : '',
            loading && !isMinimal ? 'opacity-0' : '',
            className,
          ].join(' ')}
          style={listInlineStyle}
          {...(isMinimal
            ? {
                onMouseDown: onMouseDown,
                ref: dragEl,
              }
            : {})}
        >
          {conditions.map((item, index) =>
            renderItem ? (
              renderItem(item)
            ) : (
              <ConditionItem
                key={item.id}
                id={item.id}
                title={item.title}
                link={item.link}
                isActive={item?.title === activeCondition}
                isLast={index === conditions?.length - 1}
                isMinimal={isMinimal}
                updateScroll={updateScroll}
              />
            ),
          )}
        </ul>
      ) : (
        <Paragraph className={transitionClassNames}>
          {translations['No results were found']}
        </Paragraph>
      )}
    </>
  );
};

export default ConditionsList;
